"use client";

import { JsonLdScript } from "next-seo";
import { absoluteUrl } from "@/lib/site-url";
import { carDetailPath } from "@/lib/car-slug";
import { vendorDetailPath } from "@/lib/vendor-slug";
import {
  buildCatalogProductDescription,
  buildRentalOfferExtras,
} from "@/lib/product-offer-schema";

type CarListingItem = {
  id: string;
  slug?: string | null;
  title: string;
  brand: string;
  model: string;
  year: number;
  price_per_day: number;
  currency: string;
  images?: string[] | null;
  is_available: boolean;
  vendor?: { id: string; company_name: string; slug?: string | null } | null;
};

interface CatalogJsonLdProps {
  cars: CarListingItem[];
  canonicalUrl: string;
  /** Агрегированные оценки с БД — только при реальных отзывах (политика Google по разметке). */
  reviewAggregates?: Record<string, { ratingValue: number; reviewCount: number }>;
  /** Человекочитаемые name/description у ItemList (лучше совпадают с языком страницы). */
  listName?: string;
  listDescription?: string;
}

/** JSON-LD для каталога автомобилей - ItemList + Offer схемы */
export default function CatalogJsonLd({
  cars,
  canonicalUrl,
  reviewAggregates,
  listName = "Каталог автомобилей в аренду в Душанбе и Таджикистане",
  listDescription = "Rent a car Dushanbe, car rental Tajikistan - полный каталог автомобилей с ценами за день",
}: CatalogJsonLdProps) {
  const itemListElements = cars.slice(0, 20).map((car, index) => {
    const agg = reviewAggregates?.[car.id];
    return {
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Product",
      "@id": absoluteUrl(carDetailPath(car)),
      url: absoluteUrl(carDetailPath(car)),
      name: car.title,
      description: buildCatalogProductDescription(car),
      brand: {
        "@type": "Brand",
        name: car.brand,
      },
      model: car.model,
      vehicleModelDate: String(car.year),
      image: car.images?.[0] ? absoluteUrl(car.images[0]) : undefined,
      ...(agg && agg.reviewCount > 0
        ? {
            aggregateRating: {
              "@type": "AggregateRating" as const,
              ratingValue: agg.ratingValue,
              reviewCount: agg.reviewCount,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
      offers: {
        "@type": "Offer",
        url: absoluteUrl(carDetailPath(car)),
        priceCurrency: car.currency,
        price: car.price_per_day,
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: car.price_per_day,
          priceCurrency: car.currency,
          unitText: "DAY",
        },
        availability: car.is_available
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        ...buildRentalOfferExtras(car.currency),
        ...(car.vendor
          ? {
              seller: {
                "@type": "Organization" as const,
                name: car.vendor.company_name,
                url: absoluteUrl(
                  vendorDetailPath({
                    id: car.vendor.id,
                    slug: car.vendor.slug,
                    company_name: car.vendor.company_name,
                  })
                ),
              },
            }
          : {}),
      },
    },
  };
  });

  return (
    <JsonLdScript
      scriptKey="catalog-itemlist"
      data={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${canonicalUrl}#itemlist`,
        url: canonicalUrl,
        name: listName,
        description: listDescription,
        numberOfItems: cars.length,
        itemListElement: itemListElements,
      }}
    />
  );
}
